import { Search, User, UserPlus, Loader2, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Customer, NewCustomerForm } from "./types";

interface CustomerStepProps {
  customerMode: "search" | "new";
  setCustomerMode: (mode: "search" | "new") => void;
  customerSearch: string;
  setCustomerSearch: (value: string) => void;
  customersLoading: boolean;
  filteredCustomers: Customer[];
  selectedCustomer: Customer | null;
  setSelectedCustomer: (customer: Customer) => void;
  newCustomer: NewCustomerForm;
  setNewCustomer: (value: NewCustomerForm | ((prev: NewCustomerForm) => NewCustomerForm)) => void;
  handleCreateCustomer: () => void;
  createCustomerPending: boolean;
  canProceedToDetails: boolean;
  onBack: () => void;
  onNext: () => void;
}

export default function CustomerStep({
  customerMode,
  setCustomerMode,
  customerSearch,
  setCustomerSearch,
  customersLoading,
  filteredCustomers,
  selectedCustomer,
  setSelectedCustomer,
  newCustomer,
  setNewCustomer,
  handleCreateCustomer,
  createCustomerPending,
  canProceedToDetails,
  onBack,
  onNext,
}: CustomerStepProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
        {t("common.fields.customer")}
      </h2>

      {/* Mode toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => setCustomerMode("search")}
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            customerMode === "search"
              ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
          }`}
        >
          <User className="h-4 w-4" />
          {t("orders.new.existingCustomer")}
        </button>
        <button
          onClick={() => setCustomerMode("new")}
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            customerMode === "new"
              ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
          }`}
        >
          <UserPlus className="h-4 w-4" />
          {t("orders.new.newCustomer")}
        </button>
      </div>

      {customerMode === "search" ? (
        <>
          {/* Customer search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t("orders.new.searchCustomers")}
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Customers list */}
          {customersLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="text-primary-600 h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {filteredCustomers.slice(0, 10).map((customer) => (
                <button
                  key={customer.id}
                  onClick={() => setSelectedCustomer(customer)}
                  className={`flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors ${
                    selectedCustomer?.id === customer.id
                      ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                      : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                  }`}
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {customer.firstName} {customer.lastName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {customer.email}
                      {customer.phone && ` | ${customer.phone}`}
                    </p>
                  </div>
                  {selectedCustomer?.id === customer.id && (
                    <Check className="text-primary-600 h-5 w-5" />
                  )}
                </button>
              ))}
              {filteredCustomers.length === 0 && customerSearch && (
                <p className="py-4 text-center text-gray-500">
                  {t("orders.new.customerNotFound")}{" "}
                  <button
                    onClick={() => {
                      setCustomerMode("new");
                      setNewCustomer((prev) => ({
                        ...prev,
                        email: customerSearch.includes("@") ? customerSearch : "",
                      }));
                    }}
                    className="text-primary-600 hover:underline"
                  >
                    {t("orders.new.createNew")}
                  </button>
                </p>
              )}
            </div>
          )}
        </>
      ) : (
        /* New customer form */
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("orders.new.firstNameRequired")}
            </label>
            <input
              type="text"
              value={newCustomer.firstName}
              onChange={(e) => setNewCustomer({ ...newCustomer, firstName: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("orders.new.lastNameRequired")}
            </label>
            <input
              type="text"
              value={newCustomer.lastName}
              onChange={(e) => setNewCustomer({ ...newCustomer, lastName: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("orders.new.emailRequired")}
            </label>
            <input
              type="email"
              value={newCustomer.email}
              onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("common.fields.phone")}
            </label>
            <input
              type="tel"
              value={newCustomer.phone}
              onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
              className="input"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("orders.new.street")}
            </label>
            <input
              type="text"
              value={newCustomer.street}
              onChange={(e) => setNewCustomer({ ...newCustomer, street: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("orders.new.city")}
            </label>
            <input
              type="text"
              value={newCustomer.city}
              onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("orders.new.zip")}
            </label>
            <input
              type="text"
              value={newCustomer.zip}
              onChange={(e) => setNewCustomer({ ...newCustomer, zip: e.target.value })}
              className="input"
            />
          </div>
          <div className="sm:col-span-2">
            <button
              onClick={handleCreateCustomer}
              disabled={
                !newCustomer.email ||
                !newCustomer.firstName ||
                !newCustomer.lastName ||
                createCustomerPending
              }
              className="btn-primary"
            >
              {createCustomerPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="mr-2 h-4 w-4" />
              )}
              {t("orders.new.createCustomer")}
            </button>
          </div>
        </div>
      )}

      {/* Selected customer info */}
      {selectedCustomer && (
        <div className="bg-success-50 dark:bg-success-900/20 rounded-lg p-4">
          <p className="text-success-700 dark:text-success-400 font-medium">
            {t("orders.new.selectedCustomer", {
              name: `${selectedCustomer.firstName ?? ""} ${selectedCustomer.lastName ?? ""}`.trim(),
              email: selectedCustomer.email,
            })}
          </p>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <button onClick={onBack} className="btn-ghost">
          {t("common.actions.back")}
        </button>
        <button onClick={onNext} disabled={!canProceedToDetails} className="btn-primary">
          {t("orders.new.continue")}
        </button>
      </div>
    </div>
  );
}
